import { AbstractDestroyable, AbstractStore, SingleIndex, VoidCallback } from '@deqo/foundation';
import { ClientRef, EventRouter, TopicData } from '@deqo/podopolis';
import { EditorPodSession } from '../../app/canvas/EditorPodSession';
import { TopicSubscription } from './TopicSubscription';

class Subscription<T extends TopicData> extends AbstractStore<T> implements TopicSubscription<T> {

    protected state: T = ({} as any);

    private router = new EventRouter<{
        broadcast: [ClientRef, string, any],
    }>();

    constructor(public readonly topic: string, private pod: EditorPodSession, onDestroy: VoidCallback) {
        super();
        this.burden(
            onDestroy,
            pod.on('topicBroadcast', ([target, data, sender]) => {
                if (target === topic && Array.isArray(data)) {
                    this.router.emit('broadcast', [sender, ...data] as any);
                }
            }),
            pod.on('topicUpdate', ([target, data]) => {
                if (target === topic) {
                    this.setState(data as any);
                }
            }),
            this.router,
        );
    }

    public waitForFirstUpdate() {
        return new Promise<void>((rs) => {
            const cancel = this.observe(() => {
                cancel();
                rs();
            });
        });
    }

    public broadcast(type: string, data: any = {}) {
        this.guardDestroyed();
        this.pod.broadcastTopic(this.topic, [type, data]);
    }

    public reset(data: T, opts?: { notify?: boolean }) {
        this.guardDestroyed();
        this.setState(this.pod.resetTopic(this.topic, data) as any, opts);
    }

    public patch(values: Partial<T>, opts?: { notify?: boolean }) {
        this.guardDestroyed();
        this.setState(this.pod.patchTopic(this.topic, values) as any, opts);
    }

    public receive(broadcastType: string, callback: (sender: ClientRef, body: any) => void) {
        this.guardDestroyed();
        return this.router.on('broadcast', ([sender, type, data]) => {
            if (broadcastType === type) {
                callback(sender, data);
            }
        });
    }
}

export class TopicManager extends AbstractDestroyable {

    private readonly subscriptions = new SingleIndex<string, Subscription<any>>();

    constructor(private pod: EditorPodSession) {
        super();
    }

    public subscribe<T extends TopicData = TopicData>(topic: string): Promise<TopicSubscription<T>> {
        // This odd order of exec can't be done with async/await
        return new Promise((rs) => {
            const sub: Subscription<T> = new Subscription<T>(topic, this.pod, () => this.onRelease(sub));
            this.subscriptions.add(topic, sub);
            sub.waitForFirstUpdate().then(() => rs(sub));
            this.pod.subscribeTopic(topic);
        });
    }

    private onRelease<T>(sub: Subscription<T>) {
        this.subscriptions.remove(sub.topic, sub);
        // If this was the last subscription instance, forget the topic.  We do it this way because topics can only be subscribed to once
        // per pod, so we have one real subscription serving many subscription objects.
        if (this.subscriptions.has(sub.topic)) {
            this.pod.forgetTopic(sub.topic);
        }
    }
}
